import api from '@/services/api';
import type { CampaignConfigSetting } from '../types/campaign.types';
import type { BannerRulesFormValues } from '../schemas/bannerRules.schema';

const smrPath = '/smr';

const KEYS = { enabled: 'banner_enabled', severity: 'banner_severity', text: 'banner_text' } as const;

export interface BannerSettingRow {
    key: string;
    value: string | boolean;
    config_option_id?: string;
}

/** Seed the form from the loaded settings. Missing rows -> defaults (enabled=false). */
export const settingsToBannerForm = (settings: CampaignConfigSetting[]): BannerRulesFormValues => {
    const byKey = new Map(settings.map((s) => [s.key, s.value]));
    return {
        banner_enabled: Boolean(byKey.get(KEYS.enabled) ?? false),
        banner_severity: String(byKey.get(KEYS.severity) ?? ''),
        banner_text: String(byKey.get(KEYS.text) ?? ''),
    };
};

/**
 * Build the attach payload. Always write banner_enabled; write severity + text ONLY when
 * enabled (Decision D2 — disabling leaves prior authored severity/text untouched so
 * re-enabling restores them). config_option_id sourced from the catalogue map (D3).
 */
export const bannerFormToSettings = (
    values: BannerRulesFormValues,
    optionIdByKey: Record<string, string | undefined>,
): BannerSettingRow[] => {
    const rows: BannerSettingRow[] = [
        { key: KEYS.enabled, value: values.banner_enabled, config_option_id: optionIdByKey[KEYS.enabled] },
    ];
    if (values.banner_enabled) {
        rows.push({ key: KEYS.severity, value: values.banner_severity, config_option_id: optionIdByKey[KEYS.severity] });
        rows.push({ key: KEYS.text, value: values.banner_text, config_option_id: optionIdByKey[KEYS.text] });
    }
    return rows;
};

/**
 * Single POST attach call — idempotent upsert-by-key (CampaignConfigSettingService::attach).
 * Mirrors fetchCampaignConfigSettings's envelope-unwrap.
 */
export const saveBannerConfigSettings = async (
    campaignId: string,
    rows: BannerSettingRow[],
): Promise<CampaignConfigSetting[]> => {
    const response = await api.post(`${smrPath}/campaigns/${campaignId}/config-settings`, { settings: rows });
    return (response.data.data ?? response.data) as CampaignConfigSetting[];
};
